import React from "react";
import { Paper, Text, Stack, Flex, Box } from "@mantine/core";
import {
  IconHome,
  IconUsers,
  IconTools,
  IconCreditCard,
  IconWallet,
  IconWorld,
  IconCoins,
  IconPercentage,
  IconReceipt,
  IconShield,
  IconReceiptTax,
  IconClipboardList,
  IconFileText,
  IconFolder,
  IconPackage,
  IconStack2,
  IconGift,
  IconBell,
  IconRosetteDiscount,
  IconTag,
  IconMessageCircleExclamation,
} from "@tabler/icons-react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import StudioManagement from "./StudioManagement";
import StaffManagement from "./StaffManagementSettings";
import ServicesManagement from "./ServicesManagementSettings";
import PaymentSettings from "./PaymentSettings";
import OffPlatformPaymentsSettings from "./OffPlatformPaymentsSettings";
import DepositSettings from "./DepositSettings";
import GiftCardSettings from "./GiftCardSettings";
import PackagesSettingsTab from "./PackagesSettingsTab";
import DiscountPresetsSettings from "./discounts/DiscountPresetsSettings";
import AppointmentTagsSettings from "./appointmentTags/AppointmentTagsSettings";
import CommissionsManagement from "./CommissionsManagement";
import StaffTiersSettings from "./StaffTiersSettings";
import PoliciesSettings from "./PoliciesSettings";
import PermissionsSettings from "./PermissionsSettings";
import NotificationSettings from "./notifications/NotificationSettings";
import TaxesSettings from "./TaxesSettings";
import CheckoutReminderSettings from "./CheckoutReminderSettings";
import SubscriptionSettingsPage from "./SubscriptionSettingsPage";
import FormTemplatesSettingsTab from "./FormTemplatesSettingsTab";
import SalonDocumentsSettingsTab from "./SalonDocumentsSettingsTab";
import WebsiteGeneratorLandingPage from "../StaticWebGenerator/components/WebsiteGeneratorLandingPage";
import { SettingsNav } from "./common";
import "./SettingsPage.css";
import { useMediaQuery } from "@mantine/hooks";
import { useSalon } from "../../context/SalonContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { isProductionEnvironment } from "../StaticWebGenerator/utils/FeatureLocks";

// Coming Soon component for Website tab in production
const WebsiteComingSoon = () => (
  <Stack align="center" justify="center" py="xl" gap="md">
    <IconWorld size={48} color="var(--mantine-color-teal-6)" />
    <Text size="lg" c="dimmed" ta="center">
      We're working hard to bring you a powerful website builder. Stay tuned for
      updates!
    </Text>
  </Stack>
);

// Ordered settings nav categories. Membership is declared on each menu item
// via its `group` id (see `menuItems` below). A group only renders when it has
// at least one visible (non-hidden) item, so entitlement-gated items and their
// groups (Forms & Waivers, Website, etc.) hide with no extra feature flags.
const SETTINGS_GROUPS = [
  { id: "business", label: "Business" },
  { id: "offerings", label: "Offerings" },
  { id: "team", label: "Team" },
  { id: "forms", label: "Forms & Waivers" },
  { id: "checkout", label: "Checkout" },
];

function SettingsPage({ onBusinessHoursSubmit, onServicesSubmit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { currentUser, businessInfo } = useSalon();
  const { access } = useFeatureAccess();

  const showComingSoon = isProductionEnvironment();

  // Section permissions from /access endpoint
  const perms = access?.section_permissions;

  const menuItems = [
    {
      id: "studio",
      label: "My Business",
      group: "business",
      icon: <IconHome size="1.2rem" />,
      path: "/admin/settings/studio",
      element: <StudioManagement onSubmit={onBusinessHoursSubmit} />,
      hidden: perms?.settingsStudio?.canView === false,
    },
    {
      id: "staff",
      label: "Staff",
      group: "team",
      icon: <IconUsers size="1.2rem" />,
      path: "/admin/settings/staff",
      element: <StaffManagement />,
      hidden: perms?.settingsStaff?.scope === "none",
    },
    {
      id: "staff-tiers",
      label: "Staff Tiers",
      group: "team",
      icon: <IconStack2 size="1.2rem" />,
      path: "/admin/settings/staff-tiers",
      element: <StaffTiersSettings />,
      // Top-level setting. Visible to roles that can manage or assign tiers
      // (Owners/Managers by default); hidden for roles with neither permission.
      hidden:
        perms?.staffTiers?.canCreateTiers === false &&
        perms?.staffTiers?.canAssignStaffToTiers === false,
    },
    {
      id: "services",
      label: "Services",
      group: "offerings",
      icon: <IconTools size="1.2rem" />,
      path: "/admin/settings/services",
      element: <ServicesManagement onSubmit={onServicesSubmit} />,
      hidden: perms?.settingsServices?.scope === "none",
    },
    {
      id: "appointment-tags",
      label: "Appointment Tags",
      group: "offerings",
      icon: <IconTag size="1.2rem" />,
      path: "/admin/settings/appointment-tags",
      element: <AppointmentTagsSettings />,
      // Every staff member can list tags (needed to apply them); the page
      // itself gates add/edit/delete on calendar.canManageAppointmentTags.
    },
    {
      id: "website",
      label: "Website",
      group: "business",
      icon: <IconWorld size="1.2rem" />,
      path: "/admin/settings/website",
      element: showComingSoon ? (
        <WebsiteComingSoon />
      ) : (
        <WebsiteGeneratorLandingPage />
      ),
      hidden: perms?.settingsSSG?.canAccess === false,
    },
    {
      id: "payments",
      label: "Payments",
      group: "checkout",
      icon: <IconCreditCard size="1.2rem" />,
      path: "/admin/settings/payments",
      element: <PaymentSettings />,
      hidden: perms?.payments?.canView === false,
    },
    {
      id: "off-platform-payments",
      label: "Off-Platform Payments",
      group: "checkout",
      icon: <IconWallet size="1.2rem" />,
      path: "/admin/settings/off-platform-payments",
      element: (
        <OffPlatformPaymentsSettings
          readOnly={perms?.settingsStudio?.canEdit === false}
        />
      ),
      hidden: perms?.settingsStudio?.canView === false,
    },
    {
      id: "deposits",
      label: "Deposits",
      group: "checkout",
      icon: <IconCoins size="1.2rem" />,
      path: "/admin/settings/deposits",
      element: <DepositSettings />,
      hidden: perms?.settingsDeposits?.canAccess === false,
    },
    {
      id: "gift-cards",
      label: "Gift Cards",
      group: "checkout",
      icon: <IconGift size="1.2rem" />,
      path: "/admin/settings/gift-cards",
      element: <GiftCardSettings />,
      // Ships dark behind the env flag; hidden until it's on. Once on, hide only
      // on an explicit permission deny (an unset section still shows).
      hidden:
        process.env.REACT_APP_GIFT_CARDS_ENABLED !== "true" ||
        perms?.settingsGiftCards?.canAccess === false,
    },
    {
      id: "commissions",
      label: "Commissions",
      group: "team",
      icon: <IconPercentage size="1.2rem" />,
      path: "/admin/settings/commissions",
      element: <CommissionsManagement onSubmit={onBusinessHoursSubmit} />,
      hidden: perms?.settingsCommissions?.scope === "none",
    },
    {
      id: "packages",
      label: "Packages & Specials",
      group: "offerings",
      icon: <IconPackage size="1.2rem" />,
      path: "/admin/settings/packages",
      element: <PackagesSettingsTab />,
      hidden: perms?.settingsServices?.scope === "none",
    },
    {
      id: "discounts",
      label: "Discounts",
      group: "offerings",
      icon: <IconRosetteDiscount size="1.2rem" />,
      path: "/admin/settings/discounts",
      element: <DiscountPresetsSettings />,
      // Every role can at least view the presets (they're needed to apply
      // discounts); hide only when the role is explicitly denied both managing
      // and deleting them.
      hidden:
        perms?.settingsDiscounts?.canManagePresets === false &&
        perms?.settingsDiscounts?.canDeletePresets === false,
    },
    {
      id: "taxes",
      label: "Taxes",
      group: "checkout",
      icon: <IconReceiptTax size="1.2rem" />,
      path: "/admin/settings/taxes",
      element: <TaxesSettings />,
    },
    {
      id: "checkout-reminders",
      label: "Checkout Reminders",
      group: "checkout",
      icon: <IconMessageCircleExclamation size="1.2rem" />,
      path: "/admin/settings/checkout-reminders",
      element: (
        <CheckoutReminderSettings
          readOnly={perms?.settingsStudio?.canEdit === false}
        />
      ),
      // Edits the salon record, so it shares Off-Platform Payments' gate.
      hidden: perms?.settingsStudio?.canView === false,
    },
    {
      id: "policies",
      label: "Policies",
      group: "business",
      icon: <IconFileText size="1.2rem" />,
      path: "/admin/settings/policies",
      element: <PoliciesSettings />,
      hidden: currentUser?.role !== "Owner" && currentUser?.role !== "Manager",
    },
    {
      id: "form-templates",
      label: "Form Templates",
      group: "forms",
      icon: <IconClipboardList size="1.2rem" />,
      path: "/admin/settings/form-templates",
      element: <FormTemplatesSettingsTab />,
      // PHI surface — backend zeros canViewTemplates for salons that don't
      // qualify. Hide unless explicitly granted (deny-by-default).
      hidden: perms?.formsAndWaivers?.canViewTemplates !== true,
    },
    {
      id: "documents",
      label: "Documents",
      group: "forms",
      icon: <IconFolder size="1.2rem" />,
      path: "/admin/settings/documents",
      element: <SalonDocumentsSettingsTab />,
      hidden: perms?.formsAndWaivers?.canViewTemplates !== true,
    },
    {
      id: "notifications",
      label: "Notifications",
      group: "team",
      icon: <IconBell size="1.2rem" />,
      path: "/admin/settings/notifications",
      element: <NotificationSettings />,
      hidden: perms?.settingsNotifications?.scope === "none",
    },
    {
      id: "permissions",
      label: "Permissions",
      group: "team",
      icon: <IconShield size="1.2rem" />,
      path: "/admin/settings/permissions",
      element: <PermissionsSettings />,
      hidden: currentUser?.role !== "Owner" && currentUser?.role !== "Manager",
    },
  ];

  // Group the visible menu items into ordered categories for the nav. Items
  // keep their existing `hidden` permission gate; a group with no visible items
  // is dropped so its header never renders on its own.
  const navGroups = SETTINGS_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: menuItems.filter((item) => item.group === group.id && !item.hidden),
  })).filter((group) => group.items.length > 0);

  return (
    <Stack p={0} gap={0} className="settings-page">
      {!isMobile && (
        <Box className="settings-panes">
          <Flex h="100%" align="stretch">
            <Paper
              className="menu-list"
              py="lg"
              px="sm"
              withBorder
              w="25%"
              miw={200}
              maw="17rem"
            >
              <SettingsNav
                title="Settings"
                groups={navGroups}
                activePath={location.pathname}
                onNavigate={navigate}
                layout="sidebar"
              />
            </Paper>

            <Paper className="settings-content-pane" p="xs" withBorder>
              <Routes>
                {menuItems.map(
                  (item) =>
                    !item.hidden && (
                      <Route
                        key={item.id}
                        path={item.id}
                        element={item.element}
                      />
                    ),
                )}
                <Route
                  path="subscription"
                  element={<SubscriptionSettingsPage />}
                />
                <Route
                  path="*"
                  element={
                    <StudioManagement onSubmit={onBusinessHoursSubmit} />
                  }
                />
              </Routes>
            </Paper>
          </Flex>
        </Box>
      )}

      {isMobile && (
        <Stack gap={0}>
          <Paper p="xs" w="100%" h="fit-content" withBorder>
            <SettingsNav
              title="Settings"
              groups={navGroups}
              activePath={location.pathname}
              onNavigate={navigate}
              layout="bar"
            />
          </Paper>
          <Routes>
            {menuItems.map(
              (item) =>
                !item.hidden && (
                  <Route key={item.id} path={item.id} element={item.element} />
                ),
            )}
            <Route path="subscription" element={<SubscriptionSettingsPage />} />
            <Route
              path="*"
              element={<StudioManagement onSubmit={onBusinessHoursSubmit} />}
            />
          </Routes>
        </Stack>
      )}
    </Stack>
  );
}

export default SettingsPage;
